import React, { useState } from 'react';
import { updateProfile } from 'firebase/auth';
import { auth, uploadFile, compressImageForUpload, signOutUser } from '../firebase';
import { initializePushNotificationsForUser, requestWebPushPermissionWithUserGesture } from '../messaging';
import { DEFAULT_AVATAR } from '../utils/defaultAvatar';
import { getSocket, isSocketConnected } from '../socket';
import { BUBBLE_COLORS, DEFAULT_BUBBLE_COLOR, isValidBubbleColor } from '../utils/bubbleColors';
import './Settings.css';

function Settings({ user, onClose, myBubbleColor, onBubbleColorChange, archivedRooms = [], onOpenArchivedChat, onUnarchiveChat }) {
  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [photoURL, setPhotoURL] = useState(user.photoURL || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewURL, setPreviewURL] = useState(user.photoURL || DEFAULT_AVATAR);
  const [bubbleColor, setBubbleColor] = useState(isValidBubbleColor(myBubbleColor) ? myBubbleColor : DEFAULT_BUBBLE_COLOR);
  const [soundNotificationsEnabled, setSoundNotificationsEnabled] = useState(() => {
    try {
      return localStorage.getItem('tchat_sound_notifications') !== 'false'; // Default to true
    } catch (_) {
      return true;
    }
  });

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be smaller than 5MB');
        return;
      }

      setPhotoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewURL(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const socket = getSocket();
      if (!socket || !isSocketConnected()) {
        setError('Not connected. Please try again in a moment.');
        return;
      }

      let newPhotoURL = photoURL;

      // Upload new photo if selected
      if (photoFile) {
        const compressed = await compressImageForUpload(photoFile, { maxDimension: 512, quality: 0.82 });
        const path = `profile-pictures/${user.uid}/${Date.now()}_${compressed.name}`;
        newPhotoURL = await uploadFile(compressed, path, {
          cacheControl: 'public, max-age=604800',
          contentType: compressed.type
        });
      }

      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        displayName: displayName.trim(),
        photoURL: newPhotoURL
      });

      // Persist bubble color on server (visible to everyone)
      const safeBubbleColor = isValidBubbleColor(bubbleColor) ? bubbleColor : DEFAULT_BUBBLE_COLOR;
      socket.emit('user:bubbleColor:set', { bubbleColor: safeBubbleColor });
      onBubbleColorChange?.(safeBubbleColor);
      try {
        localStorage.setItem('tchat_bubble_color', safeBubbleColor);
      } catch (_) {}

      // Save sound notifications preference
      try {
        localStorage.setItem('tchat_sound_notifications', soundNotificationsEnabled.toString());
      } catch (_) {}

      setSuccess('Settings updated successfully!');
      setPhotoFile(null);
      
      // Refresh the page after a short delay to reflect changes
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPreviewURL(DEFAULT_AVATAR);
    setPhotoURL('');
  };

  const handleEnableNotifications = async () => {
    try {
      // First, gate permission behind user gesture
      const permResult = await requestWebPushPermissionWithUserGesture();
      if (permResult && permResult.granted) {
        await initializePushNotificationsForUser(auth.currentUser);
      }
      
      // Check the current permission status
      const currentPermission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
      
      if (currentPermission === 'granted') {
        setSuccess('Notifications enabled successfully!');
        setError('');
      } else if (currentPermission === 'denied') {
        const deniedCount = localStorage.getItem('notification_denied_count') || '0';
        const deniedCountNum = parseInt(deniedCount);
        
        if (deniedCountNum >= 3) {
          setError('Notifications are blocked by your browser. Please enable them manually in your browser settings.');
        } else {
          setError('Notifications were denied. Please allow notifications when prompted.');
        }
      } else if (currentPermission === 'default') {
        setSuccess('Notification permission requested. Please allow notifications when prompted.');
        setError('');
      } else {
        setError('Notifications are not supported in this browser.');
      }
    } catch (e) {
      console.error('Enable notifications failed:', e);
      setError('Failed to enable notifications.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
    } catch (e) {
      console.error('Error signing out:', e);
      setError('Failed to sign out.');
    }
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-container" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Account Settings</h2>
          <button onClick={onClose} className="close-btn" title="Close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="settings-form">
          <div className="settings-grid">
            <div className="settings-card">
              <div className="photo-section">
                <div className="photo-preview">
                  <img src={previewURL} alt="Profile" className="preview-image" />
                </div>
                <div className="photo-actions">
                  <label className="upload-btn">
                    Choose Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                      disabled={loading}
                    />
                  </label>
                  {(photoFile || photoURL) && (
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="remove-photo-btn"
                      disabled={loading}
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
                <p className="photo-hint">JPG, PNG or GIF. Max size 5MB.</p>
              </div>
            </div>

            <div className="settings-card">
              <div className="form-section tight">
                <div className="form-group">
                  <label htmlFor="displayName">Display Name</label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your display name"
                    className="settings-input"
                    required
                    disabled={loading}
                    maxLength={50}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={user.email}
                    className="settings-input"
                    disabled
                    readOnly
                  />
                  <p className="input-hint">Email cannot be changed</p>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="form-section tight">
                <div className="form-group">
                  <label>Notifications</label>
                  <div className="inline-actions">
                    <button
                      type="button"
                      onClick={handleEnableNotifications}
                      className="save-btn"
                      disabled={loading}
                    >
                      {typeof Notification !== 'undefined' && Notification.permission === 'granted' 
                        ? 'Re-enable notifications' 
                        : 'Enable notifications'
                      }
                    </button>
                    <span className="input-hint">
                      Status: {typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'}
                      {typeof Notification !== 'undefined' && Notification.permission === 'denied' && 
                        localStorage.getItem('notification_denied_count') && 
                        parseInt(localStorage.getItem('notification_denied_count')) >= 3 && 
                        ' (blocked by browser)'
                      }
                    </span>
                  </div>
                  {typeof Notification !== 'undefined' && Notification.permission === 'denied' && 
                    parseInt(localStorage.getItem('notification_denied_count') || '0') >= 3 && (
                    <p className="warning-text">
                      Notifications are blocked. Click the notification icon in your browser's address bar to enable them.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="form-section tight">
                <div className="form-group">
                  <label>Sound notifications</label>
                  <div className="inline-actions">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={soundNotificationsEnabled}
                        onChange={(e) => setSoundNotificationsEnabled(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="input-hint">
                      Play sound when receiving messages on other tabs
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="form-section tight">
                <div className="form-group">
                  <label>Chat bubble color</label>
                  <div className="bubble-color-palette" role="radiogroup" aria-label="Chat bubble color">
                    {Object.entries(BUBBLE_COLORS).map(([key, hex]) => (
                      <button
                        key={key}
                        type="button"
                        className={`bubble-color-swatch ${bubbleColor === key ? 'selected' : ''}`}
                        style={{ backgroundColor: hex }}
                        onClick={() => setBubbleColor(key)}
                        aria-checked={bubbleColor === key}
                        role="radio"
                        title={key}
                      />
                    ))}
                  </div>
                  <span className="input-hint">Saved when you click “Save Changes”. Visible to everyone.</span>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="form-section tight">
                <div className="form-group">
                  <label>Archived chats</label>
                  {archivedRooms.length === 0 ? (
                    <span className="input-hint">No archived chats</span>
                  ) : (
                    <div className="archived-chats-list">
                      {archivedRooms.map(room => (
                        <div key={room.roomId} className="archived-chat-row">
                          <span className="archived-chat-name">{room.name}</span>
                          <button
                            type="button"
                            className="archived-chat-open"
                            onClick={() => onOpenArchivedChat?.(room)}
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            className="archived-chat-unarchive"
                            onClick={() => onUnarchiveChat?.(room)}
                            title="Move back to the chat list"
                          >
                            Unarchive
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="form-section tight">
                <div className="form-group">
                  <label>Session</label>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="logout-btn"
                    disabled={loading}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="settings-error">{error}</div>}
          {success && <div className="settings-success">{success}</div>}

          <div className="settings-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={loading || !displayName.trim()}
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Settings;

